import { Box, Typography } from '@mui/material';
import {
  RadioQuestion,
  type RadioOption,
} from 'src/suitability/engine/components/radio-question';
import type { TestStepProps } from 'src/suitability/engine/types';
import type { LiftAnswers, ScoredQuestionKey } from './answers';
import { toRadioOptions, useLiftContent } from './content';

/** Deja a un bloque tocar las opciones de una pregunta antes de pintarlas (gating). */
type OptionsDecorator = (
  question: ScoredQuestionKey,
  options: RadioOption[],
) => RadioOption[];

interface ScoredBlockProps extends TestStepProps<LiftAnswers> {
  stepKey: string;
  questions: readonly ScoredQuestionKey[];
  decorate?: OptionsDecorator;
}

/**
 * Un bloque puntuable: el título del paso y una pregunta de opción única por cada
 * clave, con enunciado y opciones del catálogo.
 *
 * Las 25 preguntas de Lift son radios, así que los tres bloques son el MISMO
 * componente y sólo el de objetivos añade reglas. Nada que ver con Wio, donde cada
 * bloque tiene su propia forma (quiz, verdadero/falso, multiselección).
 */
export const ScoredBlock = ({
  answers,
  onUpdate,
  stepKey,
  questions,
  decorate,
}: ScoredBlockProps) => {
  const content = useLiftContent();
  const step = content.step(stepKey);

  return (
    <Box>
      <Typography variant="textXl" sx={{ fontWeight: 700, mb: 1 }}>
        {step.title}
      </Typography>
      {step.intro && (
        <Typography
          variant="textSm"
          sx={{ color: 'text.secondary', mb: 3, display: 'block' }}
        >
          {step.intro}
        </Typography>
      )}

      {questions.map((question) => {
        const { prompt } = content.question(question);
        const options = toRadioOptions(question, content.question(question));
        const answer = answers[question];

        return (
          <RadioQuestion
            key={question}
            question={prompt}
            options={decorate ? decorate(question, options) : options}
            value={answer === undefined ? undefined : String(answer)}
            onChange={(value) =>
              onUpdate({ [question]: Number(value) } as Partial<LiftAnswers>)
            }
            required
          />
        );
      })}
    </Box>
  );
};
